import * as XLSX from 'xlsx';

import type {
  AudienciaPresencialItem,
  AudienciaRemotaItem,
  DesignacaoEspecialAudienciaItem,
  DesignacaoEspecialProcessoItem,
} from '@/modules/reports/presentation/pages/relatorio-civel-page.models';

import { celulaExcelComoTexto } from '@/modules/reports/presentation/utils/numero-processo-excel-import.utils';

function normalizarCabecalho(c: string): string {
  return c
    .replace(/\u00A0/g, ' ')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

export function parseDataPlanilha(texto: string): Date | null {
  const t = celulaExcelComoTexto(texto);
  if (!t) return null;
  if (/^\d{4}-\d{2}-\d{2}/.test(t)) {
    const d = new Date(t.slice(0, 10));
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const m = t.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})/);
  if (m) {
    const dia = parseInt(m[1], 10);
    const mes = parseInt(m[2], 10) - 1;
    const ano = parseInt(m[3], 10);
    const d = new Date(ano, mes, dia);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const d2 = new Date(t);
  if (Number.isNaN(d2.getTime()) || d2.getFullYear() < 1900) return null;
  return d2;
}

export function resolverTipoListaAudiencia(texto: string, tipos: readonly string[]): { valor: string | null } {
  const t = texto.trim();
  if (!t) return { valor: null };
  const low = t.toLowerCase();
  const exato = tipos.find((x) => x.trim().toLowerCase() === low);
  if (exato) return { valor: exato };
  const fuzzy = tipos.find(
    (x) => low.includes(x.trim().toLowerCase()) || x.trim().toLowerCase().includes(low),
  );
  if (fuzzy) return { valor: fuzzy };
  return { valor: null };
}

export function resolverDefensoriaOpcao(
  texto: string,
  opcoes: { value: number; label: string }[],
): { id: number | null; label: string | null } {
  const t = texto.trim();
  if (!t) return { id: null, label: null };
  if (/^\d+$/.test(t.trim())) {
    const idNum = parseInt(t.trim(), 10);
    const byVal = opcoes.find((o) => o.value === idNum);
    if (byVal) return { id: byVal.value, label: byVal.label };
  }
  const low = t.toLowerCase();
  const labelEq = opcoes.find((o) => o.label.trim().toLowerCase() === low);
  if (labelEq) return { id: labelEq.value, label: labelEq.label };
  const labelFuzzy = opcoes.find(
    (o) => o.label.toLowerCase().includes(low) || low.includes(o.label.trim().toLowerCase()),
  );
  if (labelFuzzy) return { id: labelFuzzy.value, label: labelFuzzy.label };
  return { id: null, label: null };
}

function n(k: string) {
  return normalizarCabecalho(k);
}

function chaveData(keys: string[]): string | null {
  return (
    keys.find((k) => n(k) === 'data') ||
    keys.find((k) => n(k).startsWith('data ')) ||
    keys.find((k) => n(k).includes('data') && !n(k).includes('protocolo')) ||
    null
  );
}

function chaveNumeroProcesso(keys: string[], exclude: Set<string>): string | null {
  return (
    keys.find((k) => !exclude.has(k) && n(k).includes('numero') && n(k).includes('processo')) ||
    keys.find((k) => !exclude.has(k) && n(k).includes('processo') && !n(k).includes('protocolo')) ||
    null
  );
}

function chaveProtocoloOuNumeroPresencial(keys: string[], exclude: Set<string>): string | null {
  return (
    keys.find((k) => !exclude.has(k) && n(k).includes('protocolo')) ||
    keys.find((k) => !exclude.has(k) && n(k).includes('numero') && n(k).includes('protocol')) ||
    keys.find((k) => !exclude.has(k) && n(k).includes('n') && n(k).includes('protocol')) ||
    keys.find((k) => !exclude.has(k) && n(k).includes('numero') && !n(k).includes('processo')) ||
    null
  );
}

function chaveTipoAudiencia(keys: string[], exclude: Set<string>): string | null {
  return (
    keys.find((k) => !exclude.has(k) && n(k).includes('audiencia')) ||
    keys.find((k) => !exclude.has(k) && n(k).includes('tipo') && (n(k).includes('aud') || n(k).includes('peca'))) ||
    keys.find((k) => !exclude.has(k) && (n(k) === 'tipo' || n(k).startsWith('tipo '))) ||
    null
  );
}

function chaveDefensoria(keys: string[], exclude: Set<string>): string | null {
  return (
    keys.find((k) => !exclude.has(k) && (n(k).includes('defensoria') || n(k).includes('vara'))) ||
    null
  );
}

function chaveTipoPeca(keys: string[], exclude: Set<string>): string | null {
  return (
    keys.find((k) => !exclude.has(k) && n(k).includes('tipo') && (n(k).includes('peca') || n(k).includes('peça'))) ||
    keys.find((k) => !exclude.has(k) && n(k).includes('peca') && !n(k).includes('processo')) ||
    keys.find((k) => !exclude.has(k) && n(k).includes('tipo')) ||
    null
  );
}

/** Alvo da importação Excel das etapas 8, 14 e 15. */
export type ExcelImportEtapas81415Alvo =
  | { tipo: 'audiencias-presencial' }
  | { tipo: 'audiencias-remota' }
  | { tipo: 'designacao-processo' }
  | { tipo: 'designacao-audiencia' };

export type LinhaPreviewAudienciaPresencial = {
  indicePlanilha: number;
  tipoTexto: string;
  tipoResolvido: string | null;
  numeroProtocolo: string;
  dataTexto: string;
  data: Date | null;
  valido: boolean;
  erros: string[];
};

export type LinhaPreviewAudienciaRemota = {
  indicePlanilha: number;
  tipoTexto: string;
  tipoResolvido: string | null;
  numeroProcesso: string;
  dataTexto: string;
  data: Date | null;
  valido: boolean;
  erros: string[];
};

export type LinhaPreviewDesignacaoProcesso = {
  indicePlanilha: number;
  tipoPeca: string;
  numeroProcesso: string;
  defensoriaTexto: string;
  defensoriaId: number | null;
  defensoriaLabel: string | null;
  valido: boolean;
  erros: string[];
};

export type LinhaPreviewDesignacaoAudiencia = {
  indicePlanilha: number;
  audienciaTexto: string;
  numeroProcesso: string;
  dataTexto: string;
  data: Date | null;
  defensoriaTexto: string;
  defensoriaId: number | null;
  defensoriaLabel: string | null;
  valido: boolean;
  erros: string[];
};

function cabecalhoAudPresencial(keys: string[]): { tipo: string; protocolo: string; data: string } | null {
  const used = new Set<string>();
  const dataK = chaveData(keys);
  if (!dataK) return null;
  used.add(dataK);
  const protoK = chaveProtocoloOuNumeroPresencial(keys, used);
  if (!protoK) return null;
  used.add(protoK);
  const tipoK = chaveTipoAudiencia(keys, used);
  if (!tipoK) return null;
  return { tipo: tipoK, protocolo: protoK, data: dataK };
}

export function parseAudienciasPresencialExcel(
  arrayBuffer: ArrayBuffer,
  tiposAudiencia: readonly string[],
): { linhas: LinhaPreviewAudienciaPresencial[]; erroCabeçalho: string | null } {
  const wb = XLSX.read(arrayBuffer, { type: 'array' });
  const sheet = wb.Sheets[wb.SheetNames[0]];
  const rows = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, { defval: '', raw: false });
  if (rows.length === 0) return { linhas: [], erroCabeçalho: 'A planilha está vazia.' };
  const keys = Object.keys(rows[0]);
  const chaves = cabecalhoAudPresencial(keys);
  if (!chaves) {
    return {
      linhas: [],
      erroCabeçalho:
        'Cabeçalhos não identificados. Use na 1.ª linha colunas reconhecíveis como Audiência/Tipo, Número/Protocolo e Data.',
    };
  }

  const linhas: LinhaPreviewAudienciaPresencial[] = [];
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    const tipoTexto = celulaExcelComoTexto(row[chaves.tipo]);
    const numeroProtocolo = celulaExcelComoTexto(row[chaves.protocolo]);
    const dataTexto = celulaExcelComoTexto(row[chaves.data]);
    if (!tipoTexto && !numeroProtocolo && !dataTexto) continue;

    const erros: string[] = [];
    const { valor: tipoResolvido } = resolverTipoListaAudiencia(tipoTexto, tiposAudiencia);
    if (!tipoTexto) erros.push('Tipo de audiência em branco.');
    else if (!tipoResolvido) erros.push('Tipo de audiência não reconhecido (use um dos tipos do sistema).');
    if (!numeroProtocolo.trim()) erros.push('Número/protocolo em branco.');
    const data = parseDataPlanilha(dataTexto);
    if (!dataTexto.trim()) erros.push('Data em branco.');
    else if (!data) erros.push('Data inválida (use AAAA-MM-DD ou DD/MM/AAAA).');

    linhas.push({
      indicePlanilha: i + 2,
      tipoTexto,
      tipoResolvido,
      numeroProtocolo,
      dataTexto,
      data,
      valido: erros.length === 0,
      erros,
    });
  }
  return { linhas, erroCabeçalho: null };
}

function cabecalhoAudRemota(keys: string[]): { tipo: string; processo: string; data: string } | null {
  const used = new Set<string>();
  const dataK = chaveData(keys);
  if (!dataK) return null;
  used.add(dataK);
  const procK = chaveNumeroProcesso(keys, used);
  if (!procK) return null;
  used.add(procK);
  const tipoK = chaveTipoAudiencia(keys, used);
  if (!tipoK) return null;
  return { tipo: tipoK, processo: procK, data: dataK };
}

export function parseAudienciasRemotaExcel(
  arrayBuffer: ArrayBuffer,
  tiposAudiencia: readonly string[],
): { linhas: LinhaPreviewAudienciaRemota[]; erroCabeçalho: string | null } {
  const wb = XLSX.read(arrayBuffer, { type: 'array' });
  const sheet = wb.Sheets[wb.SheetNames[0]];
  const rows = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, { defval: '', raw: false });
  if (rows.length === 0) return { linhas: [], erroCabeçalho: 'A planilha está vazia.' };
  const keys = Object.keys(rows[0]);
  const chaves = cabecalhoAudRemota(keys);
  if (!chaves) {
    return {
      linhas: [],
      erroCabeçalho:
        'Cabeçalhos não identificados. Use na 1.ª linha colunas como Audiência/Tipo, Número do processo e Data.',
    };
  }

  const linhas: LinhaPreviewAudienciaRemota[] = [];
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    const tipoTexto = celulaExcelComoTexto(row[chaves.tipo]);
    const numeroProcesso = celulaExcelComoTexto(row[chaves.processo]);
    const dataTexto = celulaExcelComoTexto(row[chaves.data]);
    if (!tipoTexto && !numeroProcesso && !dataTexto) continue;

    const erros: string[] = [];
    const { valor: tipoResolvido } = resolverTipoListaAudiencia(tipoTexto, tiposAudiencia);
    if (!tipoTexto) erros.push('Tipo de audiência em branco.');
    else if (!tipoResolvido) erros.push('Tipo de audiência não reconhecido (use um dos tipos do sistema).');
    if (!numeroProcesso.trim()) erros.push('Número do processo em branco.');
    const data = parseDataPlanilha(dataTexto);
    if (!dataTexto.trim()) erros.push('Data em branco.');
    else if (!data) erros.push('Data inválida (use AAAA-MM-DD ou DD/MM/AAAA).');

    linhas.push({
      indicePlanilha: i + 2,
      tipoTexto,
      tipoResolvido,
      numeroProcesso,
      dataTexto,
      data,
      valido: erros.length === 0,
      erros,
    });
  }
  return { linhas, erroCabeçalho: null };
}

function cabecalhoDesigProcesso(keys: string[]): { tipo: string; processo: string; defensoria: string } | null {
  const used = new Set<string>();
  const defK = chaveDefensoria(keys, used);
  if (!defK) return null;
  used.add(defK);
  const procK = chaveNumeroProcesso(keys, used);
  if (!procK) return null;
  used.add(procK);
  const tipoK = chaveTipoPeca(keys, used);
  if (!tipoK) return null;
  return { tipo: tipoK, processo: procK, defensoria: defK };
}

export function parseDesignacaoProcessoExcel(
  arrayBuffer: ArrayBuffer,
  defensoriasOptions: { value: number; label: string }[],
): { linhas: LinhaPreviewDesignacaoProcesso[]; erroCabeçalho: string | null } {
  const wb = XLSX.read(arrayBuffer, { type: 'array' });
  const sheet = wb.Sheets[wb.SheetNames[0]];
  const rows = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, { defval: '', raw: false });
  if (rows.length === 0) return { linhas: [], erroCabeçalho: 'A planilha está vazia.' };
  const keys = Object.keys(rows[0]);
  const chaves = cabecalhoDesigProcesso(keys);
  if (!chaves) {
    return {
      linhas: [],
      erroCabeçalho:
        'Cabeçalhos não identificados. Use na 1.ª linha colunas como Tipo de peça, Número do processo e Defensoria/Vara.',
    };
  }

  const linhas: LinhaPreviewDesignacaoProcesso[] = [];
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    const tipoPeca = celulaExcelComoTexto(row[chaves.tipo]);
    const numeroProcesso = celulaExcelComoTexto(row[chaves.processo]);
    const defensoriaTexto = celulaExcelComoTexto(row[chaves.defensoria]);
    if (!tipoPeca && !numeroProcesso && !defensoriaTexto) continue;

    const erros: string[] = [];
    if (!tipoPeca.trim()) erros.push('Tipo de peça em branco.');
    if (!numeroProcesso.trim()) erros.push('Número do processo em branco.');
    const { id: defensoriaId, label: defensoriaLabel } = resolverDefensoriaOpcao(defensoriaTexto, defensoriasOptions);
    if (!defensoriaTexto.trim()) erros.push('Defensoria em branco.');
    else if (defensoriaId == null) erros.push('Defensoria não encontrada (use o mesmo texto da lista ou o código).');

    linhas.push({
      indicePlanilha: i + 2,
      tipoPeca,
      numeroProcesso,
      defensoriaTexto,
      defensoriaId,
      defensoriaLabel,
      valido: erros.length === 0,
      erros,
    });
  }
  return { linhas, erroCabeçalho: null };
}

function cabecalhoDesigAudiencia(keys: string[]): { tipo: string; processo: string; data: string; defensoria: string } | null {
  const used = new Set<string>();
  const dataK = chaveData(keys);
  if (!dataK) return null;
  used.add(dataK);
  const defK = chaveDefensoria(keys, used);
  if (!defK) return null;
  used.add(defK);
  const procK = chaveNumeroProcesso(keys, used);
  if (!procK) return null;
  used.add(procK);
  const tipoK = chaveTipoPeca(keys, used) || chaveTipoAudiencia(keys, used);
  if (!tipoK) return null;
  return { tipo: tipoK, processo: procK, data: dataK, defensoria: defK };
}

export function parseDesignacaoAudienciaExcel(
  arrayBuffer: ArrayBuffer,
  defensoriasOptions: { value: number; label: string }[],
): { linhas: LinhaPreviewDesignacaoAudiencia[]; erroCabeçalho: string | null } {
  const wb = XLSX.read(arrayBuffer, { type: 'array' });
  const sheet = wb.Sheets[wb.SheetNames[0]];
  const rows = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, { defval: '', raw: false });
  if (rows.length === 0) return { linhas: [], erroCabeçalho: 'A planilha está vazia.' };
  const keys = Object.keys(rows[0]);
  const chaves = cabecalhoDesigAudiencia(keys);
  if (!chaves) {
    return {
      linhas: [],
      erroCabeçalho:
        'Cabeçalhos não identificados. Use na 1.ª linha colunas como Audiência/Tipo, Número do processo, Data e Defensoria/Vara.',
    };
  }

  const linhas: LinhaPreviewDesignacaoAudiencia[] = [];
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    const audienciaTexto = celulaExcelComoTexto(row[chaves.tipo]);
    const numeroProcesso = celulaExcelComoTexto(row[chaves.processo]);
    const dataTexto = celulaExcelComoTexto(row[chaves.data]);
    const defensoriaTexto = celulaExcelComoTexto(row[chaves.defensoria]);
    if (!audienciaTexto && !numeroProcesso && !dataTexto && !defensoriaTexto) continue;

    const erros: string[] = [];
    if (!audienciaTexto.trim()) erros.push('Audiência/tipo em branco.');
    if (!numeroProcesso.trim()) erros.push('Número do processo em branco.');
    const data = parseDataPlanilha(dataTexto);
    if (!dataTexto.trim()) erros.push('Data em branco.');
    else if (!data) erros.push('Data inválida (use AAAA-MM-DD ou DD/MM/AAAA).');
    const { id: defensoriaId, label: defensoriaLabel } = resolverDefensoriaOpcao(defensoriaTexto, defensoriasOptions);
    if (!defensoriaTexto.trim()) erros.push('Defensoria em branco.');
    else if (defensoriaId == null) erros.push('Defensoria não encontrada (use o mesmo texto da lista ou o código).');

    linhas.push({
      indicePlanilha: i + 2,
      audienciaTexto,
      numeroProcesso,
      dataTexto,
      data,
      defensoriaTexto,
      defensoriaId,
      defensoriaLabel,
      valido: erros.length === 0,
      erros,
    });
  }
  return { linhas, erroCabeçalho: null };
}

export function montarAudienciasPresencialImportadas(linhas: LinhaPreviewAudienciaPresencial[]): AudienciaPresencialItem[] {
  return linhas
    .filter((l) => l.valido && l.tipoResolvido && l.data)
    .map((l, i) => ({
      id: `import-${Date.now()}-${i}-${Math.random().toString(36).slice(2, 9)}`,
      audienciaPresencial: l.tipoResolvido!,
      numeroProtocolo: l.numeroProtocolo.trim(),
      data: l.data!,
    }));
}

export function montarAudienciasRemotaImportadas(linhas: LinhaPreviewAudienciaRemota[]): AudienciaRemotaItem[] {
  return linhas
    .filter((l) => l.valido && l.tipoResolvido && l.data)
    .map((l, i) => ({
      id: `import-${Date.now()}-${i}-${Math.random().toString(36).slice(2, 9)}`,
      audienciaRemota: l.tipoResolvido!,
      numeroProcesso: l.numeroProcesso.trim(),
      data: l.data!,
    }));
}

export function montarDesignacaoProcessoImportadas(
  linhas: LinhaPreviewDesignacaoProcesso[],
  getDefensoriaLabel: (id: number) => string,
): DesignacaoEspecialProcessoItem[] {
  const agora = new Date();
  return linhas
    .filter((l) => l.valido && l.defensoriaId != null)
    .map((l, i) => ({
      id: `import-${Date.now()}-${i}-${Math.random().toString(36).slice(2, 9)}`,
      tipoPeca: l.tipoPeca.trim(),
      numeroProcesso: l.numeroProcesso.trim(),
      defensoriaId: l.defensoriaId!,
      defensoriaLabel: l.defensoriaLabel ?? getDefensoriaLabel(l.defensoriaId!),
      dataAdicao: agora,
    }));
}

export function montarDesignacaoAudienciaImportadas(
  linhas: LinhaPreviewDesignacaoAudiencia[],
  getDefensoriaLabel: (id: number) => string,
): DesignacaoEspecialAudienciaItem[] {
  const agora = new Date();
  return linhas
    .filter((l) => l.valido && l.defensoriaId != null && l.data)
    .map((l, i) => ({
      id: `import-${Date.now()}-${i}-${Math.random().toString(36).slice(2, 9)}`,
      audiencia: l.audienciaTexto.trim(),
      numeroProcesso: l.numeroProcesso.trim(),
      data: l.data!,
      defensoriaId: l.defensoriaId!,
      defensoriaLabel: l.defensoriaLabel ?? getDefensoriaLabel(l.defensoriaId!),
      dataAdicao: agora,
    }));
}
